import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listOrganizationsByType,
  previewOrgStatement,
  issueOrgStatement,
  listOrgStatements,
  downloadOrgStatement,
} from '../../api/organizations.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

/**
 * Value & Billing statements — ICER platform admin (value/billing Phase 3d).
 * Pick an org + named period, PREVIEW the assembled statement (services
 * tagged Covered/Billable, subscription access, over-delivery, engagement,
 * totals), then ISSUE it — which freezes the snapshot and renders the PDF —
 * and download. Issued statements are listed with download links.
 */

const K = (x) => `K${Number(x || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

function quarter(year, q) {
  const ends = { 1: ['01-01', '03-31'], 2: ['04-01', '06-30'], 3: ['07-01', '09-30'], 4: ['10-01', '12-31'] }
  const [s, e] = ends[q]
  return [`${year}-${s}`, `${year}-${e}`]
}

export default function OrgStatementsPage() {
  const qc = useQueryClient()
  const thisYear = new Date().getFullYear()
  const [orgId, setOrgId] = useState(null)
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [preview, setPreview] = useState(null)

  const orgsQ = useQuery({ queryKey: ['orgs-list'], queryFn: () => listOrganizationsByType() })
  const listQ = useQuery({
    queryKey: ['org-statements', orgId],
    queryFn: () => listOrgStatements(orgId),
    enabled: Boolean(orgId),
  })

  const previewMut = useMutation({
    mutationFn: () => previewOrgStatement({ orgId, periodStart: start, periodEnd: end }),
    onSuccess: (data) => setPreview(data),
  })
  const issueMut = useMutation({
    mutationFn: () => issueOrgStatement({ orgId, periodStart: start, periodEnd: end, snapshot: preview }),
    onSuccess: () => {
      setPreview(null)
      qc.invalidateQueries({ queryKey: ['org-statements', orgId] })
    },
  })

  async function download(id, label) {
    const blob = await downloadOrgStatement(id)
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `wantok_statement_${label || id}.pdf`
    document.body.appendChild(a); a.click(); a.remove()
    window.URL.revokeObjectURL(url)
  }

  const setQuarter = (q) => { const [s, e] = quarter(thisYear, q); setStart(s); setEnd(e); setPreview(null) }
  const canRun = orgId && start && end

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Value & Billing statements"
          subtitle="Assemble a per-organisation statement for a named period, then issue it as a PDF. Covered services count as delivered value; Billable add-ons sum to the amount owed; the platform access fee is shown separately."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          <Field label="Organisation">
            <select value={orgId || ''} onChange={(e) => { setOrgId(e.target.value ? Number(e.target.value) : null); setPreview(null) }}
              className="w-full border border-sand-300 rounded-md px-3 py-1.5 text-sm bg-white">
              <option value="">Select…</option>
              {(orgsQ.data || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Period start">
            <input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreview(null) }}
              className="w-full border border-sand-300 rounded-md px-3 py-1.5 text-sm bg-white" />
          </Field>
          <Field label="Period end">
            <input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreview(null) }}
              className="w-full border border-sand-300 rounded-md px-3 py-1.5 text-sm bg-white" />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">{thisYear} quarters:</span>
          {[1, 2, 3, 4].map((q) => (
            <button key={q} onClick={() => setQuarter(q)}
              className="text-xs px-2.5 py-1 rounded-full border border-sand-300 text-slate-600 hover:bg-sand-50">Q{q}</button>
          ))}
          <div className="flex-1" />
          <button disabled={!canRun || previewMut.isPending} onClick={() => previewMut.mutate()}
            className="px-3 py-1.5 rounded-md border border-wantok-300 text-wantok-700 text-sm hover:bg-wantok-50 disabled:opacity-50">
            {previewMut.isPending ? 'Assembling…' : 'Preview'}
          </button>
        </div>
        {previewMut.error && <div className="mt-2"><ErrorBanner error={previewMut.error} /></div>}
      </Card>

      {preview && (
        <Card>
          <CardHeader
            title={`Preview — ${preview.organization.name} · ${preview.period.label}`}
            subtitle={`${preview.period.start} to ${preview.period.end} · ${preview.header.staff_served} staff served of ${preview.header.active_members} members`}
            right={
              <button disabled={issueMut.isPending} onClick={() => issueMut.mutate()}
                className="text-xs font-semibold px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 disabled:opacity-50">
                {issueMut.isPending ? 'Issuing…' : 'Issue statement'}
              </button>
            }
          />
          {issueMut.error && <ErrorBanner error={issueMut.error} />}
          <StatementBody stmt={preview} />
        </Card>
      )}

      {orgId && (
        <Card>
          <CardHeader title="Issued statements"
            subtitle="Frozen point-in-time. Download the PDF or hand it to the org admin (who can also download their own)." />
          {(listQ.data || []).length === 0 ? (
            <div className="text-sm text-slate-400 italic py-4 text-center">No statements issued for this organisation yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr><th className="text-left p-2">Period</th><th className="text-right p-2">Billable</th>
                  <th className="text-right p-2">Value delivered</th><th className="text-left p-2">Issued</th><th className="p-2"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(listQ.data || []).map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="p-2 font-medium text-slate-800">{s.label || `${s.period_start} – ${s.period_end}`}</td>
                    <td className="p-2 text-right text-sky-700 font-semibold">{K(s.amount_billable_pgk)}</td>
                    <td className="p-2 text-right">{K(s.total_value_delivered_pgk)}</td>
                    <td className="p-2 text-slate-500">{s.issued_at ? new Date(s.issued_at).toLocaleDateString() : '—'}</td>
                    <td className="p-2 text-right">
                      <button disabled={!s.has_pdf} onClick={() => download(s.id, s.label)}
                        className="text-xs px-2.5 py-1 rounded-full border border-wantok-300 text-wantok-700 hover:bg-wantok-50 disabled:opacity-40">
                        {s.has_pdf ? '⬇ PDF' : 'No PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  )
}

function StatementBody({ stmt }) {
  const t = stmt.totals
  return (
    <div className="space-y-4 text-sm">
      {stmt.subscription_access && (
        <Section title="Subscription access">
          <Line label={`${stmt.subscription_access.label} (${stmt.subscription_access.members} members, pro-rated)`}
            value={K(stmt.subscription_access.value_pgk)} />
        </Section>
      )}

      <Section title="Services rendered">
        {stmt.services.length === 0 ? <Empty>No covered or billable services in this period.</Empty> : (
          <table className="w-full">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
              <tr><th className="text-left p-1.5">Service</th><th className="text-right p-1.5">Count</th>
                <th className="text-left p-1.5">Status</th><th className="text-right p-1.5">Value</th></tr>
            </thead>
            <tbody>
              {stmt.services.map((s) => (
                <tr key={s.service_type} className="border-b border-slate-50">
                  <td className="p-1.5">{s.service_type_label}</td>
                  <td className="p-1.5 text-right">{s.count}</td>
                  <td className="p-1.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.tag === 'billable' ? 'bg-sky-50 text-sky-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {s.tag === 'billable' ? 'Billable' : 'Covered'}
                    </span>
                  </td>
                  <td className="p-1.5 text-right">{K(s.value_pgk)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {stmt.over_delivery && stmt.over_delivery.length > 0 && (
        <Section title="Screening over-delivery">
          {stmt.over_delivery.map((c, i) => (
            <Line key={i} label={c.campaign}
              value={`${c.screened} of ${c.expected_headcount} subscribed · +${c.surplus} (${c.surplus_pct}%)`} />
          ))}
        </Section>
      )}

      <Section title="Engagement value delivered">
        <Line label="Avoided physical visits" value={stmt.engagement.avoided_visits} />
        <Line label="Travel cost saved" value={K(stmt.engagement.travel_savings)} />
        <Line label="Estimated total savings" value={K(stmt.engagement.total_savings)} />
      </Section>

      <Section title="Totals">
        <Line label="Covered services (in subscription)" value={K(t.covered_value_pgk)} />
        <Line label="Amount billable (add-ons)" value={K(t.amount_billable_pgk)} strong />
        <Line label="Platform access fee" value={K(t.platform_access_pgk)} />
        <Line label="Total value delivered (services)" value={K(t.total_value_delivered_pgk)} grand />
        <Line label="Subscription paid in period" value={K(t.subscription_paid_pgk)} />
      </Section>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-wantok-700 font-semibold border-b border-slate-200 pb-1 mb-1.5">{title}</div>
      {children}
    </div>
  )
}
function Line({ label, value, strong, grand }) {
  return (
    <div className={`flex justify-between py-1 ${grand ? 'border-t border-slate-200 mt-1 pt-1.5 font-bold text-wantok-700' : ''}`}>
      <span className={strong ? 'text-sky-700 font-semibold' : 'text-slate-600'}>{label}</span>
      <span className={`tabular-nums ${strong ? 'text-sky-700 font-semibold' : 'text-slate-800'}`}>{value}</span>
    </div>
  )
}
function Empty({ children }) { return <div className="text-slate-400 italic py-2">{children}</div> }
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  )
}
